<template>
  <BaseDialog
      :title="title"
      width="60%"
      :visible.sync="dialogVisible"
      :show-only-close-button="true"
      :append-to-body="appendToBody">
    <template slot="contents">
      <div class="dialog-contents">
        <template v-if="hasData">
          <BaseTableDetail
              v-if="showDetail"
              :table-set-list="detailHeader"
              :display-item="detailData"
              :site-code="siteCode"
              class="detail-table" />
          <BaseTableList
              v-if="showDetailList"
              :table-set-list="listHeader"
              :display-items="detailListData"
              :site-code="siteCode"
              class="detail-table-list">
            <template slot="attributes"
                slot-scope="{ row }">
              <AttributeButton
                  class="attribute-button"
                  :site-code="siteCode"
                  :attribute-values="listAttributes(row)"
                  :area-name="listAttributeAreaName"
                  :append-to-body="true"
                  page-name="order_detail" />
            </template>
            <template slot="exchanges"
                slot-scope="{ row }">
              <el-button
                  size="mini"
                  @click="onClickQtyDetail(row)">
                数量明細
              </el-button>
            </template>
          </BaseTableList>
          <BaseDialog
              title="数量明細"
              width="35%"
              :visible.sync="qtyDetailDialogVisible"
              :append-to-body="true"
              :show-footer="false">
            <template slot="contents">
              <QuantityDetail
                  type="exchange"
                  :quantity="qtyDetail" />
            </template>
          </BaseDialog>
        </template>
        <template v-else>
          <div class="no-data-message">
            {{ '詳細情報がありません' }}
          </div>
        </template>
      </div>
    </template>
    <template slot="prependFooter">
      <AttributeButton
          v-if="showAttributeButton"
          class="attribute-button"
          :site-code="siteCode"
          :attribute-values="attributesData"
          :area-name="attributeAreaName"
          :append-to-body="true"
          size=""
          page-name="order_detail" />
      <BaseButton
          v-if="showExchangeButton"
          size=""
          @click="onClickExchangeButton">
        交換情報
        <SlipDialog
            :visible.sync="exchangeDialogVisible"
            :detail-list-data="exchangeListData"
            :append-to-body="true"
            type="exchange" />
      </BaseButton>
    </template>
  </BaseDialog>
</template>

<style scoped>
.no-data-message {
  margin: 5px 15px;
}
.dialog-contents >>> table {
  width: auto;
}
.dialog-contents >>> table tr:hover > td {
  background-color: initial;
}
.dialog-contents .detail-table >>> th,
.dialog-contents .detail-table >>> td {
  padding: 5px 15px;
  font-size: 14px;
  text-align: left;
  vertical-align: top;
}
.dialog-contents .detail-table >>> th {
  min-width: 130px;
  border-bottom: initial;
}
.dialog-contents .detail-table-list {
  border-top: 1px solid #c7d3d9;
}
.dialog-contents .detail-table-list >>> .el-table {
  box-shadow: initial;
}
</style>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'

import * as define from '@/define'
import BaseButton from '@/components/atoms/BaseButton'
import x from '@/components/mixins/mixin'
import orderUtil from '@/components/mixins/orderUtil'

import { ELEMENT_TYPE } from '@/components/molecules/BaseCell'
import BaseTableList from '@/components/molecules/BaseTableList'
import BaseTableDetail from '@/components/molecules/BaseTableDetail'
import BaseDialog from '@/components/molecules/BaseDialog'

import AttributeButton from '@/components/organisms/AttributeButton'

import QuantityDetail from '@/components/pages/order/QuantityDetail'

export default {
  name: 'SlipDialog',
  components: {
    BaseButton,
    BaseTableList,
    BaseTableDetail,
    BaseDialog,
    AttributeButton,
    QuantityDetail
  },
  mixins: [x, orderUtil],
  props: {
    title: {
      type: String,
      default: '詳細'
    },
    visible: {
      type: Boolean,
      default: false
    },
    appendToBody: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: ''
    },
    detailData: {
      type: [ Object, Array ],
      default: () => ({})
    },
    detailListData: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      dialogVisible: false,
      exchangeDialogVisible: false,
      qtyDetailDialogVisible: false,
      qtyDetail: {}
    }
  },
  computed: {
    define: () => define,
    ...mapGetters('OrderDetail', {
      siteCode: 'siteCode'
    }),
    ...mapGetters({
      getServiceSiteConfig: 'getServiceSiteConfig'
    }),
    hasData () {
      return _.has(this._tableSetListType, this.type) && (_.size(this.detailData) > 0 || _.size(this.detailListData) > 0)
    },
    showDetail () {
      return _.get(this._tableSetListType, `${this.type}.detail`) != null && _.size(this.detailData) > 0
    },
    showDetailList () {
      return _.get(this._tableSetListType, `${this.type}.list`) != null && _.size(this.detailListData) > 0
    },
    detailHeader () {
      return _.get(this._tableSetListType, `${this.type}.detail`, [])
    },
    listHeader () {
      return _.get(this._tableSetListType, `${this.type}.list`, [])
    },
    showAttributeButton () {
      let funcId = null
      switch (this.type) {
        case 'paymentSet':
          funcId = define.PermissionKeys.Order.Detail.PaymentSet.Detail.ATTRIBUTE
      }
      return this.attributeAreaName != null && (funcId == null || this.$isFunctionShown(funcId))
    },
    attributeAreaName () {
      return _.get(this._tableSetListType, `${this.type}.attributeAreaName`, null)
    },
    attributesData () {
      return _.get(this.detailData, 'attributes', [])
    },
    listAttributeAreaName () {
      return _.chain(this._tableSetListType)
        .get(`${this.type}.list`, null)
        .find(val => val.path === 'attributes')
        .get('areaName')
        .value()
    },
    showExchangeButton () {
      return _.get(this._tableSetListType, `${this.type}.showExchange`, false)
    },
    exchangeListData () {
      return _.get(this.detailData, 'exchanges', [])
    },
    _tableSetListType () {
      return {
        note: {
          detail: [
            { label: '登録日時', properties: [{ path: 'createdAt', type: 'Date' }] },
            { label: '更新日時', properties: [{ path: 'updatedAt', type: 'Date' }] },
            { label: '件名', properties: [{ path: 'subject', type: 'String' }] },
            { label: '内容', properties: [{ path: 'note', type: 'String' }] }
          ],
          list: null,
          attributeAreaName: null,
          showExchange: false
        },
        export: {
          detail: [
            { label: '注文指図出荷管理番号', properties: [{ path: 'orderExportShippedNo', type: 'String' }],
              funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ExportList.Detail.EXPORT_SHIPPED_NO },
            { label: '指図可能日時', properties: [{ path: 'exportableAt', type: 'Date' }],
              funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ExportList.Detail.EXPORTABLE_AT },
            { label: '締日時', properties: [{ path: 'closedAt', type: 'Date' }],
              funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ExportList.Detail.CLOSED_AT },
            { label: '指図日時', properties: [{ path: 'exportedAt', type: 'Date' }],
              funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ExportList.Detail.EXPORTED_AT }
          ],
          list: [
            { label: 'No', path: 'position', type: 'String', width: 70 },
            { label: '商品ID', path: 'orderItem.productId', type: 'String', width: 200,
              funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ExportList.Detail.PRODUCT_ID },
            { label: '表示名称', path: 'orderItem.lineitemText', type: 'String' },
            { label: '数量', path: 'quantity', type: 'Number', class: 'right', headerClass: 'right', width: 100,
              funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ExportList.Detail.QUANTITY },
            { label: '出荷指図種類', path: 'exportType', type: 'String', codeName: 'd_order_export_item.export_type', width: 120,
              funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ExportList.Detail.EXPORT_TYPE }
          ],
          attributeAreaName: 'export',
          showExchange: true
        },
        shipped: {
          detail: [
            { label: '注文指図出荷管理番号', properties: [{ path: 'orderExportShippedNo', type: 'String' }],
              funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ShippedList.Detail.EXPORT_SHIPPED_NO },
            { label: '伝票種類', properties: [{ path: 'slipType', type: 'String', codeName: 'd_shipped.slip_type' }],
              funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ShippedList.Detail.SLIP_TYPE },
            { label: '伝票種類詳細', properties: [{ path: 'slipTypeDetail', type: 'String' }],
              funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ShippedList.Detail.SLIP_TYPE_DETAIL },
            { label: '伝票番号', properties: [{ path: 'slipNo', type: 'String' }],
              funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ShippedList.Detail.SLIP_NO },
            { label: '伝票番号(追番)', properties: [{ path: 'slipNoSuffix', type: 'String' }],
              funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ShippedList.Detail.SLIP_NO_SUFFIX },
            { label: '伝票日時', properties: [{ path: 'slipDatetime', type: 'Date' }],
              funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ShippedList.Detail.SLIP_DATETIME },
            { label: '訂正区分', properties: [{ path: 'correctType', type: 'String', codeName: 'd_shipped.correct_type' }],
              funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ShippedList.Detail.CORRECT_TYPE },
            { label: '追跡番号', properties: [{ path: 'trackingNumber', type: 'String' }],
              funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ShippedList.Detail.TRACKING_NO }
          ],
          list: [
            { label: 'No', path: 'position', type: 'String', width: 70 },
            { label: '商品ID', path: 'productId', type: 'String', width: 200,
              funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ShippedList.Detail.PRODUCT_ID },
            { label: '表示名称', path: 'orderItem.lineitemText', type: 'String' },
            { label: '指図数量', path: 'requestQuantity', type: 'Number', class: 'right', headerClass: 'right', width: 100,
              funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ShippedList.Detail.REQUEST_QTY },
            { label: '数量', path: 'quantity', type: 'Number', class: 'right', headerClass: 'right', width: 100,
              funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ShippedList.Detail.QUANTITY },
            { label: '理由コード', path: 'reasonCode', type: 'String', codeName: 'd_shipped_item.reason_code',
              funcId: define.PermissionKeys.Order.Detail.ExportAndShipped.ShippedList.Detail.REASON_CODE },
            { label: '', path: 'attributes', type: 'Slot', width: 120, areaName: 'shipped_item' }
          ],
          attributeAreaName: 'shipped',
          showExchange: false
        },
        paymentSet: {
          detail: [
            { label: '支払方法',
              properties: [{
                path: 'orderPayment.paymentMethod.name', type: 'String',
                funcId: define.PermissionKeys.Order.Detail.PaymentSet.Detail.PAYMENT_METHOD_NAME
              }]
            },
            { label: '金額',
              properties: [{
                path: 'amount', type: 'Number', precision: 0, fractionDigits: 0,
                funcId: define.PermissionKeys.Order.Detail.PaymentSet.Detail.AMOUNT
              }]
            },
            { label: '連携ID1',
              properties: [{
                path: 'orderPayment.transactionId1', type: 'String',
                funcId: define.PermissionKeys.Order.Detail.PaymentSet.Detail.TRANSACTION_ID_1
              }]
            },
            { label: '連携ID2',
              properties: [{
                path: 'orderPayment.transactionId2', type: 'String',
                funcId: define.PermissionKeys.Order.Detail.PaymentSet.Detail.TRANSACTION_ID_2
              }]
            },
            { label: '連携ID3',
              properties: [{
                path: 'orderPayment.transactionId3', type: 'String',
                funcId: define.PermissionKeys.Order.Detail.PaymentSet.Detail.TRANSACTION_ID_3
              }]
            },
            { label: '処理コード',
              properties: [{
                path: 'processCode', type: 'String', codeName: 'd_payment_set.process_code',
                funcId: define.PermissionKeys.Order.Detail.PaymentSet.Detail.PROCESS_TYPE
              }]
            },
            { label: 'ステータス',
              properties: [{
                path: 'statusCode', type: 'String', codeName: 'd_payment_set.status_code',
                funcId: define.PermissionKeys.Order.Detail.PaymentSet.Detail.STATUS
              }]
            },
            { label: '予約フラグ',
              properties: [{
                path: 'isReserved', type: 'Boolean', elementType: ELEMENT_TYPE.CHECK_BOX, single: true, label: '予約',
                funcId: define.PermissionKeys.Order.Detail.PaymentSet.Detail.IS_RESERVED
              }]
            },
            { label: 'キャンセル可能フラグ',
              properties: [{
                path: 'isCancellable', type: 'Boolean', elementType: ELEMENT_TYPE.CHECK_BOX, single: true, label: 'キャンセル可能',
                funcId: define.PermissionKeys.Order.Detail.PaymentSet.Detail.CANCELLABLE
              }]
            },
            { label: '処理予定日時',
              properties: [{
                path: 'processesAt', type: 'Date',
                funcId: define.PermissionKeys.Order.Detail.PaymentSet.Detail.PROCESSES_AT
              }]
            },
            { label: '処理日時',
              properties: [{
                path: 'processedAt', type: 'Date',
                funcId: define.PermissionKeys.Order.Detail.PaymentSet.Detail.PROCESSED_AT
              }]
            },
            { label: 'リクエスト日時',
              properties: [{
                path: 'requestedAt', type: 'Date',
                funcId: define.PermissionKeys.Order.Detail.PaymentSet.Detail.REQUESTED_AT
              }]
            },
            { label: 'レスポンス日時',
              properties: [{
                path: 'responsedAt', type: 'Date',
                funcId: define.PermissionKeys.Order.Detail.PaymentSet.Detail.RESPONSED_AT
              }]
            },
            { label: 'レスポンスコード',
              properties: [{
                path: 'responseCode', type: 'String',
                funcId: define.PermissionKeys.Order.Detail.PaymentSet.Detail.RESPONSE_CODE
              }]
            },
            { label: 'エラー理由',
              properties: [{
                path: this.errorReason, type: 'String',
                funcId: define.PermissionKeys.Order.Detail.PaymentSet.Detail.ERROR_REASON,
                elementType: ELEMENT_TYPE.FUNCTION
              }]
            }
          ],
          list: null,
          attributeAreaName: 'payment_set',
          showExchange: false
        },
        payment: {
          detail: [
            { label: '支払方法',
              properties: [{
                path: 'paymentMethod.name', type: 'String',
                funcId: define.PermissionKeys.Order.Detail.OrderInfo.PaymentTypeBreakdown.PAYMENT_METHOD_NAME
              }]
            },
            { label: '金額',
              properties: [{
                path: 'amount', type: 'Number', precision: 0, fractionDigits: 0,
                funcId: define.PermissionKeys.Order.Detail.OrderInfo.PaymentTypeBreakdown.AMOUNT
              }]
            },
            { label: '連携ID1',
              properties: [{
                path: 'transactionId1', type: 'String',
                funcId: define.PermissionKeys.Order.Detail.OrderInfo.PaymentTypeBreakdown.TRANSACTION_ID_1
              }]
            },
            { label: '連携ID2',
              properties: [{
                path: 'transactionId2', type: 'String',
                funcId: define.PermissionKeys.Order.Detail.OrderInfo.PaymentTypeBreakdown.TRANSACTION_ID_2
              }]
            },
            { label: '連携ID3',
              properties: [{
                path: 'transactionId3', type: 'String',
                funcId: define.PermissionKeys.Order.Detail.OrderInfo.PaymentTypeBreakdown.TRANSACTION_ID_3
              }]
            },
            { label: '支払状況',
              properties: [
                {
                  type: 'Number', elementType: ELEMENT_TYPE.FUNCTION,
                  path: (row) => {
                    if (_.get(row, 'uuid', '') === '') {
                      return ''
                    } else if ([define.PAYMENT_METHOD_TYPE.NO_PROCESS, define.PAYMENT_METHOD_TYPE.HOLD]
                      .includes(row.paymentMethod.methodType)) {
                      return '-'
                    }
                    return this.$_mixin_getCodeName(row.isPaid,
                      'd_order_payment.is_paid',
                      { siteCode: this.siteCode })
                  },
                  funcId: define.PermissionKeys.Order.Detail.OrderInfo.PaymentTypeBreakdown.IS_PROCESSED
                }]
            },
            { label: '決済日時',
              properties: [{
                path: 'paidAt', type: 'Date',
                funcId: define.PermissionKeys.Order.Detail.OrderInfo.PaymentTypeBreakdown.PAID_AT
              }]
            },
            { label: '支払処理対象フラグ',
              properties: [{
                path: 'isProcessed', type: 'Boolean', codeName: 'd_order_payment.is_processed',
                funcId: define.PermissionKeys.Order.Detail.OrderInfo.PaymentTypeBreakdown.IS_PROCESSED
              }]
            },
            { label: '編集可否区分',
              properties: [{
                path: 'editableType', type: 'String', codeName: 'd_order_payment.editable_type',
                funcId: define.PermissionKeys.Order.Detail.OrderInfo.PaymentTypeBreakdown.IS_RESERVED
              }]
            }
          ],
          list: null,
          attributeAreaName: 'null',
          showExchange: false
        },
        exchange: {
          detail: null,
          list: [
            { label: '交換元商品ID', path: 'srcOrderItem.productId', type: 'String' },
            { label: '交換先商品ID', path: 'destOrderItem.productId', type: 'String' },
            { label: '数量', path: 'quantity', type: 'Number', class: 'right', headerClass: 'right', width: 100 },
            { label: '', path: 'exchanges', type: 'Slot', width: 120 },
            { label: '交換再送', path: 'isStockout', elementType: ELEMENT_TYPE.CHECK_BOX, class: 'center', headerClass: 'center', width: 100 }
          ],
          attributeAreaName: null,
          showExchange: false
        }
      }
    }
  },
  watch: {
    visible (val) {
      this.dialogVisible = val
    },
    dialogVisible (val) {
      this.$emit('update:visible', val)
    }
  },
  methods: {
    listAttributes (row) {
      return _.get(row, 'row.attributes', [])
    },
    onClickExchangeButton () {
      this.exchangeDialogVisible = true
    },
    onClickQtyDetail (row) {
      this.qtyDetail = row.row
      this.qtyDetailDialogVisible = true
    },
    errorReason (row) {
      if (!row.errorReasonCode) {
        return ''
      }

      // 支払種別/エラー理由コードからエラー理由を設定する
      let errorMessage = _.get(this.getServiceSiteConfig(null, 'd_payment_set_error_reason_code',
        row.orderPayment.paymentMethod.code, row.errorReasonCode), 'v.0', '')

      if (!errorMessage) {
        errorMessage = ''
      }
      errorMessage += `(エラー理由コード：${row.errorReasonCode})`
      return errorMessage
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/pages/order/SlipDialog.vue